/**
 * SSD (Session Storage & Downloads) API Client
 *
 * API client functions for SSD artifacts endpoints.
 * Extends BaseApiClient for authentication, error handling, and type-safe requests.
 * GW-7632: Added initiateUpload (POST /ssd/artifacts) multipart upload support.
 */

import type {
  ArtifactDownloadUrlResponse,
  InitiateArtifactUploadRequest,
  InitiateArtifactUploadResponse,
  ListArtifactsParams,
  ListArtifactsResponse,
} from '@/types/ssd.types';

import { BaseApiClient } from './baseApiClient';

// ============================================================================
// API Client Class
// ============================================================================

class SsdApiClient extends BaseApiClient {
  /**
   * List artifacts for the authenticated user with optional filters
   */
  async listArtifacts(params: ListArtifactsParams = {}): Promise<ListArtifactsResponse> {
    const queryParams: Record<string, string | number | boolean | undefined> = {
      limit: params.limit ?? 100,
      ...(params.nextToken && { nextToken: params.nextToken }),
      ...(params.type && { type: params.type }),
      ...(params.sourceSessionId && { sourceSessionId: params.sourceSessionId }),
      ...(params.sourceDatasetId && { sourceDatasetId: params.sourceDatasetId }),
      ...(params.sourceQueryId && { sourceQueryId: params.sourceQueryId }),
    };
    const qs = this.buildQueryString(queryParams);
    return this.get<ListArtifactsResponse>(`/ssd/artifacts${qs}`);
  }

  /**
   * Initiate a multipart upload for a new artifact.
   * POST /ssd/artifacts — returns upload instructions (HTTP 202).
   * Flow: initiateUpload → PUT each part presignedUrl → POST completeUrl
   * GW-7632: Replaces deprecated POST /ssd/artifacts/ingest.
   */
  async initiateUpload(
    request: InitiateArtifactUploadRequest
  ): Promise<InitiateArtifactUploadResponse> {
    return this.post<InitiateArtifactUploadResponse>('/ssd/artifacts', request);
  }

  /**
   * Get a pre-signed download URL for a specific artifact
   */
  async getDownloadUrl(artifactId: string): Promise<ArtifactDownloadUrlResponse> {
    return this.get<ArtifactDownloadUrlResponse>(`/ssd/artifacts/${artifactId}/download-url`);
  }

  /**
   * Delete a specific artifact
   */
  async deleteArtifact(artifactId: string): Promise<void> {
    return this.delete<void>(`/ssd/artifacts/${artifactId}`);
  }
}

// ============================================================================
// API Client Functions (exports)
// ============================================================================

const ssdApiClient = new SsdApiClient();

// Export the API client instance as ssdApi (used by ssdSlice.ts)
export { ssdApiClient as ssdApi };

/**
 * List artifacts for the authenticated user with optional filters
 */
export async function listArtifacts(
  params: ListArtifactsParams = {}
): Promise<ListArtifactsResponse> {
  return ssdApiClient.listArtifacts(params);
}

/**
 * Initiate a multipart upload for a new artifact.
 * GW-7632: Replaces deprecated POST /ssd/artifacts/ingest.
 */
export async function initiateUpload(
  request: InitiateArtifactUploadRequest
): Promise<InitiateArtifactUploadResponse> {
  return ssdApiClient.initiateUpload(request);
}

/**
 * Get a pre-signed download URL for a specific artifact
 */
export async function getDownloadUrl(artifactId: string): Promise<ArtifactDownloadUrlResponse> {
  return ssdApiClient.getDownloadUrl(artifactId);
}

/**
 * Delete a specific artifact
 */
export async function deleteArtifact(artifactId: string): Promise<void> {
  return ssdApiClient.deleteArtifact(artifactId);
}
